import React from "react";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { RichContentEditorSection } from "./RichContentEditorSection";

export const defaultBlogForm = {
  id: 0,
  customerId: "",
  title: "",
  subtitle: "",
  shortDescription: "",
  date: "",
  authorName: "",
  image: "",
  content: ""
};

export const blogInputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15";

export function toLocalDatetime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

const fieldLabelClass = "mb-1.5 block text-xs font-medium text-slate-600";

/**
 * @param {object} p
 * @param {(files: File[]) => void} [p.onEditorImageFiles] — upload & insert; image files dropped on the editor only
 * @param {(message: string) => void} [p.onContentImportError] — e.g. toast for failed Excel import
 * @param {() => void} [p.onEditorToolbarImage] — same as toolbar “image” (e.g. open file upload)
 */
export function BlogEditorFormFields({
  form,
  setForm,
  isAdmin,
  customers,
  htmlMode,
  setHtmlMode
}) {
  const dateField = (
    <div className="min-w-0">
      <label className={fieldLabelClass} htmlFor="blog-date">
        Date
      </label>
      <input
        id="blog-date"
        type="datetime-local"
        className={blogInputClass}
        value={form.date}
        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {isAdmin ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start sm:gap-4">
          <div className="min-w-0">
            <label className={fieldLabelClass} htmlFor="blog-customer">
              Customer <span className="text-red-500">*</span>
            </label>
            <select
              id="blog-customer"
              required
              className={blogInputClass}
              value={form.customerId}
              onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
            >
              <option value="">— Select —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.code ? `(${c.code})` : ""}
                </option>
              ))}
            </select>
          </div>
          {dateField}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start sm:gap-4">
          {dateField}
          <div className="hidden min-h-0 sm:block" aria-hidden />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start sm:gap-4">
        <div className="min-w-0">
          <label className={fieldLabelClass} htmlFor="blog-title">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            id="blog-title"
            className={blogInputClass}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
          />
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClass} htmlFor="blog-subtitle">
            Subtitle
          </label>
          <input
            id="blog-subtitle"
            className={blogInputClass}
            value={form.subtitle}
            onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start sm:gap-4">
        <div className="min-w-0">
          <label className={fieldLabelClass} htmlFor="blog-short-description">
            Short description
          </label>
          <textarea
            id="blog-short-description"
            className={blogInputClass}
            rows={3}
            value={form.shortDescription}
            onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
          />
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClass} htmlFor="blog-author">
            Author name
          </label>
          <input
            id="blog-author"
            className={blogInputClass}
            value={form.authorName}
            onChange={(e) => setForm((f) => ({ ...f, authorName: e.target.value }))}
          />
        </div>
        <div className="min-w-0">
          <ImageUploadField
            label="Cover image"
            value={form.image}
            onChange={(v) => setForm((f) => ({ ...f, image: v }))}
            labelClassName="mb-1.5 block text-xs font-medium text-slate-600"
          />
        </div>
      </div>

      <RichContentEditorSection
        form={form}
        setForm={setForm}
        htmlMode={htmlMode}
        setHtmlMode={setHtmlMode}
        label="Content"
        fieldId="blog-content"
      />
    </div>
  );
}
